import React, { useEffect, useRef, useState } from 'react'
import { SpeechSynthesisPonyfillType, SpeechSynthesisUtterance } from '@davi-ai/web-speech-cognitive-services-davi'
import type { SpeechSynthesisEventProps } from '@davi-ai/web-speech-cognitive-services-davi'
import { useSpeechStore } from '../Contexts/SpeechContext'
import { useRetorikStore } from '../Contexts/RetorikContext'
import { useLocaleStore } from '../Contexts/LocaleContext'

import type { Boundary } from '../../models/speechTypes'
import { RecognitionState } from '../../models/enums'
import { audioFiles } from '../../utils/audioFiles'

import IndexedDbManager from './IndexedDbManager'
import GdprAndLoader from '../Loader/GdprAndLoader'

interface RetorikSpeechProps {
  ponyfill: SpeechSynthesisPonyfillType
  onEnd?: () => void
  onError?: () => void
  onStart?: () => void
  utterance: SpeechSynthesisUtterance | null
  nextUtterance: SpeechSynthesisUtterance | null
  appAvailable: boolean
}

interface NextUtteranceData {
  speechData: ArrayBuffer
  boundaries: Array<Boundary>
  visemes: Array<SpeechSynthesisEventProps>
}

const DBManager = new IndexedDbManager()
const tagsRemoverRegex = /<[^<>]+>/g

const RetorikSpeech = ({ ponyfill, onEnd, onError, onStart, utterance, nextUtterance, appAvailable }: RetorikSpeechProps): JSX.Element => {
  const locale = useLocaleStore((state) => state.locale)
  const loaderClosed = useRetorikStore((state) => state.loaderClosed)
  const enableSpeechCaching = useRetorikStore((state) => state.configuration.enableSpeechCaching)
  const speechRecognitionOptions = useRetorikStore((state) => state.configuration.speechRecognitionOptions)
  const useOldRemote = useRetorikStore((state) => state.configuration.useOldRemote)

  const voice = useSpeechStore((state) => state.voice)
  const muted = useSpeechStore((state) => state.muted)
  const activeRecognitionState = useSpeechStore((state) => state.activeRecognitionState)
  const { setBoundaryData, setVisemeData, addViseme } = useSpeechStore((state) => state.actions)
  const setLoaderClosed = useRetorikStore((state) => state.actions.setLoaderClosed)

  const boundaryRef = useRef<Array<Boundary> | undefined | null>(null)
  const visemeRef = useRef<Array<SpeechSynthesisEventProps> | undefined | null>(null)
  const audioRef = useRef<HTMLAudioElement>(null)
  const speechRecognitionAudioRef = useRef<HTMLAudioElement>(null)
  const microphoneOpenAudioRef = useRef<HTMLAudioElement>(null)
  const microphoneCloseAudioRef = useRef<HTMLAudioElement>(null)

  const [DBReady, setDBReady] = useState<boolean>(false)
  const [playingBlob, setPlayingBlob] = useState<boolean>(false)

  const nextUtteranceDataRef = useRef<{ text: string; data: NextUtteranceData | null } | null>(null)
  const nextUtteranceTextRef = useRef<string | null>(null)
  const nextUtteranceBoundariesRef = useRef<Array<Boundary>>([])
  const nextUtteranceVisemesRef = useRef<Array<SpeechSynthesisEventProps>>([])
  const emptyTextUtteranceRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    DBManager.checkDB().then((result) => setDBReady(result))

    return (): void => {
      stopCurrentPlaying()
      emptyTextUtteranceRef?.current && clearTimeout(emptyTextUtteranceRef.current)
    }
  }, [])

  const stopCurrentPlaying = (): void => {
    if (appAvailable || loaderClosed) {
      // Cancel synthesis if using synthesizer
      if (!playingBlob) {
        ponyfill.speechSynthesis.cancel()
      }

      resetNextUtteranceData()

      if (audioRef.current && !audioRef.current?.paused) {
        audioRef.current.pause()
        handleEnd()
      }
    }
  }

  const attachEvents = (tempUtterance: SpeechSynthesisUtterance, isMainUtterance?: boolean): SpeechSynthesisUtterance => {
    tempUtterance.onerror = (event): void => {
      isMainUtterance ? handleError(event) : (nextUtteranceDataRef.current = null)
    }

    tempUtterance.onsynthesiscompleted = (): void => {
      const endBoundary: Boundary = {
        word: '',
        startTime: 0,
        endTime: 0,
        boundaryType: 'EndBoundary'
      }

      if (isMainUtterance) {
        const tempBoundaries = boundaryRef?.current || []
        boundaryRef.current = [...tempBoundaries, endBoundary]
        setBoundaryData([...tempBoundaries, endBoundary])
      } else {
        nextUtteranceBoundariesRef.current = [...nextUtteranceBoundariesRef.current, endBoundary]
      }
    }

    tempUtterance.onboundary = (event: SpeechSynthesisEventProps): void => {
      if (event.boundaryType !== 'Viseme') {
        const newBoundary: Boundary = {
          word: event.name,
          startTime: event.elapsedTime,
          endTime: event.elapsedTime + event.duration,
          boundaryType: event.boundaryType
        }

        if (isMainUtterance) {
          // Update the boundaryData state with the new data.
          const tempBoundaries = boundaryRef?.current || []
          boundaryRef.current = [...tempBoundaries, newBoundary]
          setBoundaryData([...tempBoundaries, newBoundary])
        } else {
          nextUtteranceBoundariesRef.current = [...nextUtteranceBoundariesRef.current, newBoundary]
        }
      }
    }

    tempUtterance.onviseme = (event: SpeechSynthesisEventProps) => {
      if (isMainUtterance) {
        // Update the boundaryData state with the new data.
        addViseme(event)
        const tempVisemes = visemeRef?.current || []
        visemeRef.current = [...tempVisemes, event]
      } else {
        nextUtteranceVisemesRef.current = [...nextUtteranceVisemesRef.current, event]
      }
    }

    return tempUtterance
  }

  const processNextData = (data: ArrayBuffer) => {
    nextUtteranceDataRef.current =
      data?.byteLength && nextUtteranceTextRef.current
        ? {
            text: nextUtteranceTextRef.current,
            data: {
              speechData: data,
              boundaries: nextUtteranceBoundariesRef.current,
              visemes: nextUtteranceVisemesRef.current
            }
          }
        : null

    // Reset boundary / viseme data
    resetNextUtteranceData(true)
  }

  const resetNextUtteranceData = (keepArrayBufferData?: boolean): void => {
    !keepArrayBufferData && (nextUtteranceDataRef.current = null)
    nextUtteranceTextRef.current = null
    nextUtteranceBoundariesRef.current = []
    nextUtteranceVisemesRef.current = []
  }

  useEffect(() => {
    if (utterance) {
      if (utterance.text) {
        if (audioRef?.current) {
          if (nextUtteranceDataRef.current?.text === utterance.text && nextUtteranceDataRef.current.data) {
            boundaryRef.current = nextUtteranceDataRef.current.data.boundaries
            setBoundaryData(nextUtteranceDataRef.current.data.boundaries)
            setVisemeData(nextUtteranceDataRef.current.data.visemes)
            processData(nextUtteranceDataRef.current.data.speechData, true)
          } else {
            // Play utterance whether by asking for a speech synthesis, or by retrieving data from indexedDB
            playUtterance(attachEvents(utterance, true))
          }
        } else {
          stopCurrentPlaying()
        }
      } else {
        handleEmptyTextUtterance()
      }
    } else {
      stopCurrentPlaying()
    }
  }, [utterance])

  useEffect(() => {
    // If there are data in nextUtteranceTextRef, this means the previous synthesis data retrieval is not finished, so we don't begin a new one
    if (nextUtterance?.text && !nextUtteranceTextRef.current) {
      const tempUtterance = attachEvents(nextUtterance)
      nextUtteranceTextRef.current = nextUtterance.text
      ponyfill.speechSynthesis.synthesizeAndGetArrayData(tempUtterance, processNextData)
    } else {
      resetNextUtteranceData()
    }
  }, [nextUtterance])

  const playUtterance = async (utt: SpeechSynthesisUtterance): Promise<void> => {
    if (DBReady && enableSpeechCaching !== false && utt) {
      const dataFromIndexedDB = await DBManager.getSpeechData(`${locale}.${voice?.name}.${utt.text.replace(tagsRemoverRegex, '')}`)

      if (dataFromIndexedDB) {
        boundaryRef.current = dataFromIndexedDB.boundaries
        setBoundaryData(dataFromIndexedDB.boundaries)
        setVisemeData(dataFromIndexedDB.visemes || [])
        processData(dataFromIndexedDB.value, true)
      } else {
        ponyfill.speechSynthesis.synthesizeAndGetArrayData(utt, processData)
      }
    } else {
      ponyfill.speechSynthesis.synthesizeAndGetArrayData(utt, processData)
    }
  }

  const processData = (data: ArrayBuffer, alreadyExistsInDB?: boolean) => {
    if (data?.byteLength) {
      const blob = new Blob([data], { type: 'audio/mp3' })

      enableSpeechCaching !== false &&
        !alreadyExistsInDB &&
        DBReady &&
        boundaryRef?.current &&
        utterance &&
        DBManager.addSpeechData({
          id: `${locale}.${voice?.name}.${utterance.text.replace(tagsRemoverRegex, '')}`,
          value: data,
          boundaries: boundaryRef.current,
          visemes: visemeRef.current || []
        })

      const url = URL.createObjectURL(blob)

      if (audioRef?.current) {
        setPlayingBlob(true)
        audioRef.current.src = url
        audioRef.current.play().catch((e) => console.warn(e))
      }
    } else {
      handleEmptyTextUtterance()
    }
  }

  const resetData = (): void => {
    boundaryRef.current = []
    visemeRef.current = []
    setBoundaryData([])
    setVisemeData([])
    setPlayingBlob(false)
  }

  const handleStart = (): void => {
    if (appAvailable || loaderClosed) {
      onStart?.()
    }
  }

  const handleEnd = (): void => {
    if (appAvailable || loaderClosed) {
      onEnd?.()
      resetData()
    } else {
      setLoaderClosed(true)
    }
  }

  const handleError = (error): void => {
    console.log('Retorik Framework > speech synthesis error ', error)
    onError?.()
    resetData()
  }

  const handleEmptyTextUtterance = (): void => {
    handleStart()
    emptyTextUtteranceRef?.current && clearTimeout(emptyTextUtteranceRef.current)
    emptyTextUtteranceRef.current = setTimeout(() => {
      handleEnd()
    }, 50)
  }

  /**
   * On call :
   *  - play 1 second muted sound to prime audio output
   *  - MANDATORY FOR SAFARI IN VOCAL MODE
   */
  const primeRetorikSpeech = (): void => {
    if (audioRef.current) {
      audioRef.current.play().catch((e) => console.warn(e))
      speechRecognitionAudioRef?.current && speechRecognitionAudioRef.current.play().catch((e) => console.warn(e))

      // Send animation start event to secure animation not playing on safari if permissions are not sufficient
      window.dispatchEvent(new Event('retorikSpiritEnginePlay'))
    }
  }

  /**
   * Play sound on speech recognition activation if it isn't disabled in configuration
   */
  useEffect(() => {
    if (useOldRemote) {
      if (!!speechRecognitionOptions?.enableActivationSound && activeRecognitionState === RecognitionState.Listening) {
        speechRecognitionAudioRef?.current?.play().catch((e) => console.warn(e))
      }
    } else {
      if (!!speechRecognitionOptions?.enableActivationSound) {
        switch (activeRecognitionState) {
          case RecognitionState.Listening:
            microphoneOpenAudioRef?.current?.play().catch((e) => console.warn(e))
            break
          case RecognitionState.Closing:
            microphoneCloseAudioRef?.current?.play().catch((e) => console.warn(e))
            break
          default:
            break
        }
      }
    }
  }, [useOldRemote, activeRecognitionState, speechRecognitionOptions])

  return (
    <React.Fragment>
      <audio ref={audioRef} src={audioFiles.onesecond} muted={appAvailable || loaderClosed ? muted : true} onPlay={handleStart} onEnded={handleEnd} />
      <audio ref={speechRecognitionAudioRef} src={audioFiles.speechrecognition} preload='auto' muted={appAvailable || loaderClosed ? muted : true} />
      {/* Microphone opening sound */}
      <audio ref={microphoneOpenAudioRef} src={audioFiles.microphoneopen} preload='auto' muted={appAvailable || loaderClosed ? muted : true} />
      {/* Microphone closing sound */}
      <audio ref={microphoneCloseAudioRef} src={audioFiles.microphoneclose} preload='auto' muted={appAvailable || loaderClosed ? muted : true} />
      <GdprAndLoader appAvailable={appAvailable} handleValidation={primeRetorikSpeech} />
    </React.Fragment>
  )
}

export default RetorikSpeech
